import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, Eyebrow, MaskLines } from "@/components/site/Reveal";
import { Connect } from "@/components/site/Connect";
import { Seo } from "@/components/site/Seo";

const P = ({ children, className = "" }) => (
  <Reveal y={24}>
    <p className={`text-lg sm:text-xl leading-[1.75] text-zinc-300 ${className}`}>{children}</p>
  </Reveal>
);

export default function VidyaSri() {
  return (
    <main data-testid="vidya-page">
      <Seo
        title="Dr Vidya Sri — Co-founder | The Desi Science Page"
        description="Dr Vidya Sri is an Obstetrician & Gynaecologist, passionate teacher and Co-founder & Chief Reality Check Officer of The Desi Science Page."
        path="/people/vidya-sri"
      />
      <section className="relative px-5 sm:px-10 lg:px-16 pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -right-40 top-10 w-[520px] h-[520px] rounded-full bg-lime/10 blur-[120px]" />
        <div className="relative max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[auto_1fr] gap-10 items-end">
          <div className="order-2 md:order-1">
            <Link data-testid="back-to-people" to="/people" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.25em] text-zinc-300 hover:text-lime transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" /> The people behind this page
            </Link>
            <Eyebrow className="mb-6">// Co-founder &amp; Chief Reality Check Officer, The Desi Science Page</Eyebrow>
            <h1 data-testid="vidya-title" className="font-display font-bold uppercase leading-[0.88] text-5xl sm:text-7xl lg:text-8xl">
              <MaskLines onLoad lines={["Dr Vidya", <span className="text-lime">Sri</span>]} />
            </h1>
          </div>
          <motion.div
            data-testid="vidya-portrait"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="group order-1 md:order-2 relative w-20 sm:w-24 md:w-28 aspect-[4/5] rounded-2xl overflow-hidden border border-white/15 shadow-[0_20px_50px_-20px_rgba(200,244,90,0.35)]"
          >
            <img src="/img/vidya.jpg" alt="Dr Vidya Sri" className="w-full h-full object-cover object-top grayscale contrast-125 group-hover:grayscale-0 group-hover:contrast-100 group-hover:scale-105 transition-[filter,transform] duration-700" />
          </motion.div>
          <Reveal delay={0.4} y={20} className="order-3 md:col-span-2">
            <p data-testid="vidya-intro" className="max-w-3xl font-serif text-2xl sm:text-3xl leading-snug text-zinc-100">
              An Obstetrician &amp; Gynaecologist by profession, a passionate teacher who loves breaking down complex ideas into simple, understandable bits, and the co-conspirator with Apoorva behind many adventures down these rabbit holes.
            </p>
          </Reveal>
        </div>
      </section>
      <article className="px-5 sm:px-10 lg:px-16 pb-24">
        <div className="max-w-3xl mx-auto space-y-8 pt-8 border-t border-white/10">
          <P className="pt-8">She shares the wonder, entertains the existential questions, and often joins in questioning the universe. Equally comfortable exploring the mysteries of life and occasionally reminding Apoorva that not every question needs a 3-hour deep dive and a 27-tab literature review.</P>
          <Reveal y={24}>
            <p data-testid="vidya-highlight" className="rounded-3xl bg-panel border border-white/10 p-7 sm:p-10 font-serif text-2xl sm:text-3xl leading-snug text-white">
              The fellow explorer, voice of reason, and <span className="text-lime">the much-needed reality check</span> behind the DesiSciencePage.
            </p>
          </Reveal>
          <Reveal y={30}>
            <p data-testid="vidya-signoff" className="pt-6 font-hand text-5xl sm:text-7xl leading-tight text-lime">
              Because every rabbit hole needs someone who knows the way back!
            </p>
          </Reveal>
        </div>
      </article>
      <Connect />
    </main>
  );
}
